import { useEffect, useMemo, useState } from 'react';

import isEqual from 'lodash/isEqual';

import {
  DEFAULT_ELIGIBILITY_SETUP,
  isEligibilitySetupValid
} from 'lib/constants/healthcareProgram/eligibilitySetup';
import { useHealthcareProgramConfig } from 'lib/hooks/queries/healthcareProgram/useHealthcareProgramConfig';
import { useUpdateHealthcareProgramConfig } from 'lib/hooks/queries/healthcareProgram/useUpdateHealthcareProgramConfig';

import { showAlert } from 'components/shared/Alert/Alert';

const INVALID_ALERT = {
  color: 'danger',
  title: 'Select a team member to notify',
  message: 'The "Alert a team member" step needs an assignee before you can save.'
};

export const useEligibilitySetupDraft = ({ setIsDirty, practiceId, enabled = true } = {}) => {
  const { data, isLoading, isError } = useHealthcareProgramConfig({
    ...(practiceId != null && { practiceId }),
    enabled
  });
  const persistedSetup = data?.eligibility_setup ?? DEFAULT_ELIGIBILITY_SETUP;

  const [draftState, setDraftState] = useState(null);

  const draft = draftState ?? persistedSetup;
  const isDirty = useMemo(() => !isEqual(draft, persistedSetup), [draft, persistedSetup]);
  const isValid = useMemo(() => isEligibilitySetupValid(draft), [draft]);

  useEffect(() => {
    setIsDirty?.(isDirty);
    return () => setIsDirty?.(false);
  }, [isDirty, setIsDirty]);

  const {
    mutate: saveConfig,
    mutateAsync: saveConfigAsync,
    isLoading: isSaving
  } = useUpdateHealthcareProgramConfig({ practiceId });

  const handleThresholdChange = (value) => {
    setDraftState((prev) => {
      const base = prev ?? persistedSetup;
      return {
        ...base,
        assurance_thresholds: { ...base.assurance_thresholds, auto_finalize_gfe: value }
      };
    });
  };

  const handleStepsChange = (nextSteps) => {
    setDraftState((prev) => ({ ...(prev ?? persistedSetup), steps: nextSteps }));
  };

  const save = () => {
    if (!isValid) {
      showAlert(INVALID_ALERT);
      return;
    }
    saveConfig({ eligibility_setup: draft });
  };

  const saveAsync = async () => {
    if (!isValid) {
      showAlert(INVALID_ALERT);
      return false;
    }
    const res = await saveConfigAsync({ eligibility_setup: draft });
    return res?.code === 0;
  };

  return {
    data,
    isLoading,
    isError,
    draft,
    isDirty,
    isValid,
    isSaving,
    handleThresholdChange,
    handleStepsChange,
    save,
    saveAsync
  };
};
